import { Sun, Camera, User, ChevronDown, Shuffle, Palette, Sparkles, Wifi, Cpu, Zap } from 'lucide-react';
import { AspectRatio, ElementType, AppMode, PromptFormat } from '@/lib/engineTypes';
import { ALL_ASPECT_RATIOS, STYLE_PRESETS } from '@/lib/engineData';
import { PALETTE_PRESETS, COLOR_PALETTES, CustomPalette } from '@/data/options/colorPalettes';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef, useEffect, useMemo } from 'react';
import type { ComponentType } from 'react';
import SmartSceneInput from './SmartSceneInput';

interface Props {
  aspectRatio: AspectRatio;
  onAspectChange: (r: AspectRatio) => void;
  onAddElement: (type: ElementType) => void;
  stylePreset: string;
  onStyleChange: (s: string) => void;
  palettePreset: string;
  onPaletteChange: (s: string) => void;
  customPalette: CustomPalette | null;
  onCustomPaletteChange: (p: CustomPalette | null) => void;
  promptFormat: PromptFormat;
  onFormatChange: (f: PromptFormat) => void;
  sceneDescription: string;
  onSceneChange: (s: string) => void;
  onRawTextSubmit?: (rawText: string) => void;
  mode: AppMode;
  onModeChange: (m: AppMode) => void;
  elementCounts: { light: number; camera: number; character: number };
  graphPrompt?: string;
  onImageAnalysis?: (result: any) => void;
}

type AIMode = 'auto' | 'local' | 'pro';
const randomFrom = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

export default function Toolbar({
  aspectRatio, onAspectChange, onAddElement,
  stylePreset, onStyleChange,
  palettePreset, onPaletteChange, customPalette, onCustomPaletteChange,
  promptFormat, onFormatChange,
  sceneDescription, onSceneChange, onRawTextSubmit,
  mode, onModeChange, elementCounts,
  graphPrompt = '', onImageAnalysis,
}: Props) {
  const [showRatios, setShowRatios] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [paletteSearch, setPaletteSearch] = useState('');
  const [showCustomPalette, setShowCustomPalette] = useState(false);
  const [customName, setCustomName] = useState('My Custom Palette');
  const [draftColors, setDraftColors] = useState<string[]>(['#ffb347', '#ff9966']);
  const [newColor, setNewColor] = useState('#ffcc33');
  const [rgb, setRgb] = useState({ r: 255, g: 204, b: 51 });
  const [keywords, setKeywords] = useState('warm golden sunlight, amber highlights, sunset atmosphere');
  const [aiMode, setAiMode] = useState<AIMode>('auto');

  const ratioRef = useRef<HTMLDivElement>(null);
  const paletteRef = useRef<HTMLDivElement>(null);
  const currentRatio = ALL_ASPECT_RATIOS.find(r => r.value === aspectRatio);
  const selectedPalette = PALETTE_PRESETS.find(p => p.id === palettePreset);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (showRatios && ratioRef.current && !ratioRef.current.contains(target)) setShowRatios(false);
      if (showPalette && paletteRef.current && !paletteRef.current.contains(target)) setShowPalette(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showRatios, showPalette]);

  const filteredPalettes = useMemo(() => {
    const query = paletteSearch.trim().toLowerCase();
    if (!query) return COLOR_PALETTES;
    return COLOR_PALETTES.filter(p =>
      p.name.toLowerCase().includes(query)
      || p.mood.toLowerCase().includes(query)
      || p.keywords.toLowerCase().includes(query)
      || p.category.toLowerCase().includes(query),
    );
  }, [paletteSearch]);

  const addColorFromHex = (value: string) => {
    const clean = value.trim();
    if (!/^#[0-9a-fA-F]{6}$/.test(clean)) return;
    if (draftColors.length >= 5) return;
    setDraftColors(prev => [...prev, clean.toLowerCase()]);
  };

  const addColorFromRgb = () => {
    const hex = `#${[rgb.r, rgb.g, rgb.b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('')}`;
    addColorFromHex(hex);
  };

  const updateDraftColorAt = (index: number, value: string) => {
    const clean = value.trim().toLowerCase();
    setDraftColors((prev) => {
      const next = [...prev];
      while (next.length <= index) next.push('');
      next[index] = clean;
      return next.slice(0, 5);
    });
  };

  const saveCustomPalette = () => {
    if (draftColors.filter((c) => /^#[0-9a-fA-F]{6}$/.test(c)).length < 2) return;
    onCustomPaletteChange({
      name: customName || 'Custom Palette',
      colors: draftColors.filter((c) => /^#[0-9a-fA-F]{6}$/.test(c)).slice(0, 5),
      keywords: keywords.trim(),
    });
    setShowCustomPalette(false);
  };

  const aiModes: Array<{ id: AIMode; label: string; icon: ComponentType<{ className?: string }> }> = [
    { id: 'auto', label: 'AUTO', icon: Wifi },
    { id: 'local', label: 'LOCAL', icon: Cpu },
    { id: 'pro', label: 'PRO', icon: Zap },
  ];

  return (
    <div className="toolbar-glass panel-surface border-b border-border/40 relative z-[220] rounded-xl mx-3 mt-1.5 overflow-visible">
      <div className="flex items-center gap-1 px-2 py-1 border-b border-border/25">
        <div className="toolbar-section flex items-center p-0.5 rounded-lg border border-border/20 bg-muted/15">
          {(['basic', 'pro', 'advanced'] as const).map((level) => (
            <button
              key={level}
              onClick={() => onModeChange(level)}
              className={`px-2 py-[4px] rounded-md text-[9px] font-mono uppercase tracking-wide transition-all ${
                mode === level ? 'bg-primary/20 text-primary border border-primary/35' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {level}
            </button>
          ))}
        </div>




        <div className="relative" ref={ratioRef}>
          <button
            onClick={() => setShowRatios(!showRatios)}
            className="flex items-center gap-1 px-2 py-[4px] rounded-lg border border-border/40 bg-muted/30 text-[9px] font-mono text-foreground hover:border-border transition-all min-w-[120px] justify-between"
          >
            <span className="truncate">{currentRatio?.label || aspectRatio}</span>
            <ChevronDown className={`w-3 h-3 text-muted-foreground/40 transition-transform ${showRatios ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {showRatios && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="absolute top-full left-0 mt-1 z-[100] w-52 rounded-xl border border-border/60 bg-popover/95 backdrop-blur-xl shadow-2xl max-h-[350px] overflow-y-auto"
              >
                {['Standard', 'Portrait', 'Landscape', 'Cinematic'].map(cat => {
                  const items = ALL_ASPECT_RATIOS.filter(r => r.category === cat);
                  if (items.length === 0) return null;
                  return (
                    <div key={cat}>
                      <div className="px-3 py-1.5 text-[8px] font-mono text-primary/50 uppercase tracking-[0.2em] bg-muted/20 sticky top-0 backdrop-blur-sm">{cat}</div>
                      {items.map(r => (
                        <button
                          key={r.value}
                          onClick={() => { onAspectChange(r.value); setShowRatios(false); }}
                          className={`w-full text-left px-3 py-1.5 text-[11px] font-mono transition-colors ${aspectRatio === r.value ? 'text-primary bg-primary/10' : 'text-foreground/70 hover:text-foreground hover:bg-muted/30'}`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-1">
          <select
            value={stylePreset}
            onChange={e => onStyleChange(e.target.value)}
            className="px-2 py-[4px] rounded-lg border border-border/40 bg-muted/30 text-[9px] font-mono text-foreground hover:border-border transition-all outline-none min-w-[130px]"
          >
            {STYLE_PRESETS.map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
          </select>
          <button
            onClick={() => onStyleChange(randomFrom(STYLE_PRESETS.filter(s => s.value !== 'none')).value)}
            className="px-1 py-[4px] rounded-lg border border-border/40 bg-muted/30 text-[9px]"
            title="Random Style"
          >
            <Shuffle className="w-3 h-3" />
          </button>
        </div>

        <div className="relative" ref={paletteRef}>
          <button
            onClick={() => setShowPalette(v => !v)}
            className="flex items-center gap-1 px-2 py-[4px] rounded-lg border border-border/40 bg-muted/30 text-[9px] font-mono min-w-[130px] justify-between"
          >
            <span className="truncate">{customPalette ? `Custom: ${customPalette.name}` : `Palette: ${selectedPalette?.name ?? 'None'}`}</span>
            <ChevronDown className={`w-3 h-3 text-muted-foreground/40 transition-transform ${showPalette ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {showPalette && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="absolute top-full left-0 mt-1 z-[120] w-[360px] rounded-xl border border-border/60 bg-popover/95 backdrop-blur-xl shadow-2xl p-2"
              >
                <div className="flex gap-1 mb-2">
                  <input value={paletteSearch} onChange={(e) => setPaletteSearch(e.target.value)} placeholder="Search by name, mood, keyword..." className="flex-1 px-2 py-1.5 rounded-md border border-border/40 bg-muted/40 text-[11px]" />
                  <button onClick={() => { onCustomPaletteChange(null); onPaletteChange(randomFrom(COLOR_PALETTES).id); }} className="px-2 py-1.5 rounded-md border border-border/40 text-[10px]" title="Random Palette"><Shuffle className="w-3 h-3" /></button>
                </div>

                <div className="flex items-center gap-1 mb-2">
                  <button onClick={() => { onPaletteChange('none'); onCustomPaletteChange(null); }} className={`px-2 py-1 rounded-md text-[10px] border ${!customPalette && palettePreset === 'none' ? 'bg-primary/15 border-primary/40' : 'border-border/40'}`}>None</button>
                  <button onClick={() => setShowCustomPalette(v => !v)} className="px-2 py-1 rounded-md text-[10px] border border-border/40">Create Custom Palette</button>
                  {sceneDescription.trim() && (
                    <button
                      onClick={() => {
                        const mood = /sunset|beach|golden/i.test(sceneDescription) ? 'Golden Sunset' : /ocean|sea|water/i.test(sceneDescription) ? 'Ocean Breeze' : 'Warm Tropical';
                        const hit = COLOR_PALETTES.find(p => p.name.toLowerCase().includes(mood.toLowerCase().split(' ')[0])) || COLOR_PALETTES[0];
                        onCustomPaletteChange(null);
                        onPaletteChange(hit.id);
                      }}
                      className="px-2 py-1 rounded-md text-[10px] border border-border/40 flex items-center gap-1"
                    >
                      <Palette className="w-3 h-3" /> AI Recommend
                    </button>
                  )}
                </div>

                {showCustomPalette && (
                  <div className="border border-border/40 rounded-lg p-2 mb-2 bg-muted/30 text-[10px] space-y-2">
                    <input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="Palette name" className="w-full px-2 py-1 rounded border border-border/40 bg-background/70" />
                    <div className="grid grid-cols-1 sm:grid-cols-5 gap-1">
                      {[0, 1, 2, 3, 4].map((slot) => {
                        const value = draftColors[slot] ?? '';
                        const valid = /^#[0-9a-fA-F]{6}$/.test(value);
                        return (
                          <div key={slot} className="space-y-1">
                            <label className="text-[8px] text-muted-foreground">Color {slot + 1}</label>
                            <input value={value} onChange={(e) => updateDraftColorAt(slot, e.target.value)} placeholder="#RRGGBB" className="w-full px-1 py-1 rounded border border-border/40 bg-background/70 text-[9px]" />
                            <div className="h-4 rounded border border-black/30" style={{ backgroundColor: valid ? value : '#000000' }} />
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-1 items-center">
                      <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="w-10 h-7 rounded" />
                      <input value={newColor} onChange={(e) => setNewColor(e.target.value)} placeholder="#HEX" className="flex-1 px-2 py-1 rounded border border-border/40 bg-background/70" />
                      <button onClick={() => addColorFromHex(newColor)} className="px-2 py-1 rounded border border-border/40">Add</button>
                    </div>
                    <div className="flex gap-1 items-center">
                      <input type="number" min={0} max={255} value={rgb.r} onChange={(e) => setRgb(prev => ({ ...prev, r: Number(e.target.value) }))} className="w-16 px-1 py-1 rounded border border-border/40 bg-background/70" />
                      <input type="number" min={0} max={255} value={rgb.g} onChange={(e) => setRgb(prev => ({ ...prev, g: Number(e.target.value) }))} className="w-16 px-1 py-1 rounded border border-border/40 bg-background/70" />
                      <input type="number" min={0} max={255} value={rgb.b} onChange={(e) => setRgb(prev => ({ ...prev, b: Number(e.target.value) }))} className="w-16 px-1 py-1 rounded border border-border/40 bg-background/70" />
                      <button onClick={addColorFromRgb} className="px-2 py-1 rounded border border-border/40">RGB+</button>
                    </div>
                    <input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="Prompt keywords (description)" className="w-full px-2 py-1 rounded border border-border/40 bg-background/70" />
                    <div className="flex justify-between">
                      <span>Up to 5 colors (min 2 valid HEX)</span>
                      <button disabled={draftColors.filter((c) => /^#[0-9a-fA-F]{6}$/.test(c)).length < 2} onClick={() => { onPaletteChange('none'); saveCustomPalette(); }} className="px-2 py-1 rounded border border-primary/40 disabled:opacity-50">Save Custom</button>
                    </div>
                  </div>
                )}

                <div className="max-h-[260px] overflow-y-auto space-y-1">
                  {filteredPalettes.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { onCustomPaletteChange(null); onPaletteChange(p.id); setShowPalette(false); }}
                      className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md border text-left ${!customPalette && palettePreset === p.id ? 'border-primary/40 bg-primary/10' : 'border-border/30 bg-muted/20 hover:bg-muted/35'}`}
                      title={`${p.name} - ${p.keywords}`}
                    >
                      <div>
                        <div className="text-[11px]">{p.name}</div>
                        <div className="text-[9px] text-muted-foreground">{p.category}</div>
                      </div>
                      <div className="flex gap-1">
                        {p.colors.slice(0, 5).map((c) => <span key={c} className="w-4 h-4 rounded-sm border border-black/20" style={{ backgroundColor: c }} />)}
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <button className="px-2 py-[4px] rounded-lg border border-primary/40 bg-primary/10 text-primary text-[9px] font-mono flex items-center gap-1">
          <Sparkles className="w-2.5 h-2.5" />
          Presets
        </button>
      </div>

      <div className="px-3 py-1">
        <SmartSceneInput sceneDescription={sceneDescription} onSceneChange={onSceneChange} onRawTextSubmit={onRawTextSubmit} graphPrompt={graphPrompt} onImageAnalysis={onImageAnalysis} />
      </div>
    </div>
  );
}
